<!--
  Product details storico — pannello "Prodotti" embedded dentro
  app-storico-ordini-dettagli (NON è un modale autonomo).
  Redesigned 2026-06-15 (v2.9.0, Phase 1.5 Chunk H.5).

  Drops ion-accordion-group + ion-accordion + 2× ion-item + 2× ion-label
  + kendo-label + ~10 inline-style blocks (15 ion/kendo refs → 0).

  Bug fix legacy: il template originale aveva
  `class="example" class="col-sm-3 setDisplayToProducts"` — doppio
  attributo `class=` HTML (il secondo sovrascrive il primo). Ora
  consolidato in una sola classe.

  Pattern Diakonix card-list:
    - Product card per ciascun prodotto
    - Sub-products expand/collapse client-side (native button)
    - Action buttons inline: 🔲 QR (top-level + sub) · ℹ️ Dettagli prodotto
    - Sub-modali preserved as-is (product-detail-modal +
      products-qrcodes + subproduct-qrcode)
-->

@if (order) {
  <div class="products-panel">

    <header class="products-panel__header">
      <h2 class="products-panel__title">📦 Dettagli prodotti</h2>
      <span class="products-panel__count">
        {{ order.products?.length || 0 }}
        {{ order.products?.length === 1 ? 'prodotto' : 'prodotti' }}
      </span>
    </header>

    @if (!order.products || order.products.length === 0) {
      <div class="products-panel__empty">
        <span class="products-panel__empty-icon" aria-hidden="true">📭</span>
        <p>Nessun prodotto registrato per questo ordine.</p>
      </div>
    } @else {

      <ul class="product-list" role="list">
        @for (prod of order.products; track prod.op_code; let i = $index) {
          <li class="product-card" [class.product-card--checked]="prod.op_checked">

            <!-- Row header — sempre visibile -->
            <div class="product-card__row">
              @if (prod.sub_products?.length > 0) {
                <button
                  type="button"
                  class="product-card__chevron"
                  (click)="toggleAccordion(i)"
                  [attr.aria-expanded]="isAccordionOpen(i)"
                  [attr.aria-label]="isAccordionOpen(i) ? 'Chiudi sub-prodotti' : 'Apri sub-prodotti'"
                  [title]="isAccordionOpen(i) ? 'Chiudi sub-prodotti' : 'Apri sub-prodotti (' + prod.sub_products.length + ')'"
                >
                  @if (isAccordionOpen(i)) { ▾ } @else { ▸ }
                </button>
              } @else {
                <span class="product-card__chevron product-card__chevron--placeholder" aria-hidden="true"></span>
              }

              <div class="product-card__main">
                <span class="product-card__code">{{ prod.op_code }}</span>
                <span class="product-card__desc">{{ prod.op_description }}</span>
              </div>

              <div class="product-card__qty">
                <span class="product-card__qty-label">Qta</span>
                <span class="product-card__qty-value">{{ prod.op_qta }}</span>
              </div>

              <!--
                v2.12.8 T4: slot fissi per allineamento a colonna.
                Ogni azione ha il suo slot dedicato (details / qr).
                Se l'icona non serve per quella riga, lo slot resta
                vuoto ma occupa la sua colonna → tutte le icone di uno
                stesso tipo sono incolonnate verticalmente tra righe
                adiacenti.
              -->
              <div class="product-card__actions">
                <div class="action-slot action-slot--details">
                  @if (!prod.sub_products?.length) {
                    <button
                      type="button"
                      class="icon-btn icon-btn--primary"
                      (click)="goToProductDetails(prod.op_code)"
                      title="Dettagli prodotto"
                      aria-label="Dettagli prodotto"
                    >ℹ️</button>
                  }
                </div>
                <div class="action-slot action-slot--qr">
                  @if (hasValidBarcodes(prod)) {
                    <button
                      type="button"
                      class="icon-btn icon-btn--qr"
                      (click)="$event.stopPropagation(); seeQrCodes(prod)"
                      title="Visualizza codici QR"
                      aria-label="Visualizza codici QR del prodotto"
                    >🔲</button>
                  }
                </div>
              </div>
            </div>

            <!-- Sub-products list — espandibile se presente -->
            @if (prod.sub_products?.length > 0 && isAccordionOpen(i)) {
              <ul class="sub-product-list" role="list">
                @for (subProd of prod.sub_products; track subProd.osp_code; let j = $index) {
                  <li
                    class="sub-product-row"
                    [class.sub-product-row--checked]="subProd.osp_checked"
                  >
                    <div class="sub-product-row__main">
                      <span class="sub-product-row__code">{{ subProd.osp_code }}</span>
                      <span class="sub-product-row__desc">{{ subProd.osp_description }}</span>
                    </div>

                    <div class="sub-product-row__qty">
                      <span class="sub-product-row__qty-picked">{{ subProd.osp_qty_picked }}</span>
                      <span class="sub-product-row__qty-sep">/</span>
                      <span class="sub-product-row__qty-ordered">{{ subProd.osp_qty_ordered }}</span>
                    </div>

                    <!--
                      v2.12.8 T4: allineamento icone sub-row con la parent.
                      Slot 'details' sempre vuoto (i sub-prodotti non hanno
                      dettagli dedicati); slot 'qr' con button opzionale.
                      Le icone qr dei sub-prodotti risultano incolonnate
                      verticalmente con le qr delle parent card e con le
                      qr degli altri sub-prodotti.
                    -->
                    <div class="sub-product-row__actions">
                      <div class="action-slot action-slot--details"></div>
                      <div class="action-slot action-slot--qr">
                        @if (hasValidBarcodesSub(subProd)) {
                          <button
                            type="button"
                            class="icon-btn icon-btn--qr icon-btn--sm"
                            (click)="$event.stopPropagation(); seeQrCodesSub(subProd)"
                            title="Visualizza codici QR sub-prodotto"
                            aria-label="Visualizza codici QR del sub-prodotto"
                          >🔲</button>
                        }
                      </div>
                    </div>
                  </li>
                }
              </ul>
            }
          </li>
        }
      </ul>
    }
  </div>
}

<!-- ─── Sub-modali (preserved as-is) ──────────────────────────── -->

@if (closeServ.isProdDetOn) {
  <app-product-detail-modal
    [opened]="closeServ.isProdDetOn"
    [pr_code]="clickedProduct"
  ></app-product-detail-modal>
}

@if (closeServ.isQRProducts) {
  <app-products-qrcodes
    [openedQRS]="closeServ.isQRProducts"
    [product]="barcodes"
    [order]="order"
  ></app-products-qrcodes>
}

@if (closeServ.isQRSubProducts) {
  <app-subproduct-qrcode
    [openedSubQrs]="closeServ.isQRSubProducts"
    [subProduct]="barcodes"
    [order]="order"
  ></app-subproduct-qrcode>
}
